import React, { useEffect, useRef, useState } from 'react';
import { observer } from 'mobx-react';
import { Button, Card, Checkbox, Space } from 'antd';
import { CheckboxValueType } from 'antd/es/checkbox/Group';

import { useStores } from '@/hooks/use-stores';
import { MenuType } from '@/store/PIDEditing/PIDEditStore';
import {
  CREATE_DEFAULT_CONFIG,
  CREATE_DEVICE_CONFIGS,
} from '@/comp/MapBuilder/components/PIDEditing/DeviceDetailsCard/DynamicFormCard.utils';
import { calculateCardPosition } from '@/comp/MapBuilder/components/PIDEditing/DeviceDetailsCard/DeviceDetailsCard.utils';
import { StyledDeviceCard } from '@/comp/MapBuilder/components/PIDEditing/DeviceDetailsCard/DeviceDetailsCard.styles';
import { useTranslation } from 'react-i18next';
const PIDEditConnectionPicker: React.FC = observer(() => {
  const { pidEditStore } = useStores();
  const { activeMenu, connectedSourceDevices } = pidEditStore;
  const cardRef = useRef<HTMLDivElement>(null);
  const [cardPosition, setCardPosition] = useState({ x: 0, y: 0 });
  const { t } = useTranslation();
  const defaultCheckedList = connectedSourceDevices.map(d => d.deviceId);
  const [checkedList, setCheckedList] = useState<CheckboxValueType[]>(defaultCheckedList);

  useEffect(() => {
    const newPos = calculateCardPosition(activeMenu, cardRef);
    setCardPosition(newPos);
  }, [activeMenu]);

  useEffect(() => {
    if (connectedSourceDevices) {
      setCheckedList(connectedSourceDevices.map(d => d.deviceId));
    }
  }, [connectedSourceDevices]);

  useEffect(() => {
    // Initialize the store with the default selection on mount
    pidEditStore.setSelectedSourceDeviceIds(defaultCheckedList as string[]);
  }, [connectedSourceDevices]);

  if (!activeMenu || activeMenu.type !== MenuType.ConnectionPicker) return null;

  const deviceType = activeMenu.context?.deviceType;
  const config = (deviceType && CREATE_DEVICE_CONFIGS[deviceType]) || CREATE_DEFAULT_CONFIG;
  const title = config.title;

  const onCancel = () => {
    pidEditStore.hideMenu();
  };

  const handleNext = () => {
    pidEditStore.setDeviceState({ sourceDeviceId: checkedList as string[] });
    pidEditStore.showMenu(MenuType.CreateDevice, activeMenu.position, { deviceType: deviceType });
  };

  const onChange = (list: CheckboxValueType[]) => {
    setCheckedList(list);
    pidEditStore.setSelectedSourceDeviceIds(list as string[]);
  };

  const isNextButtonDisabled = checkedList.length === 0;

  return (
    <StyledDeviceCard ref={cardRef} position={cardPosition}>
      <Card title={title}>
        <Card.Grid hoverable={false} className="form-container" style={{ width: '100%', float: 'none' }}>
          <p>{t(' Review the associated assets and adjust your selection if necessary.')}</p>
        </Card.Grid>
        <Card.Grid hoverable={false} className="form-container" style={{ width: '100%', float: 'none' }}>
          <Checkbox.Group className="checkbox-list-container" value={checkedList} onChange={onChange}>
            {pidEditStore.connectedSourceDevices.map(device => (
              <Checkbox key={device.deviceId} value={device.deviceId}>
                {device.name}
              </Checkbox>
            ))}
          </Checkbox.Group>
        </Card.Grid>
        <div className="form-container button-container">
          <Space>
            <Button onClick={onCancel}>{t('Cancel')}</Button>
            <Button type="primary" onClick={handleNext} disabled={isNextButtonDisabled}>
              {t('Next')}
            </Button>
          </Space>
        </div>
      </Card>
    </StyledDeviceCard>
  );
});

export default PIDEditConnectionPicker;
